import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Sparkles, MessageCircle, Calendar, User, Users, Crown, ChevronDown, ChevronUp, Home, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useAccessibility } from '../contexts/AccessibilityContext';
import LanguageSelector from './LanguageSelector';
import NotificationBell from './NotificationBell';

const NavigationMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { accessibilityMode, toggleAccessibility } = useAccessibility();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  
  // Buscar contagem de notificações
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/support/unread-count`, {
          withCredentials: true,
          validateStatus: s => s === 200 || s === 401
        });
        
        if (response.status === 200) {
          setNotificationCount(response.data.count || 0);
        }
      } catch (error) {
        // Ignorar
      }
    };
    
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);
  
  // Buscar e logar status do usuário (apenas para debug)
  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/usage-status`, {validateStatus: s => s === 200 || s === 401}, {
          withCredentials: true
        });
        const plan = response.data.subscription?.plan || 'free';
        const status = response.data.subscription?.status || 'active';
        console.log(`👤 USER PLAN: ${plan.toUpperCase()} | Status: ${status}`);
      } catch (error) {
        console.error('Error fetching plan:', error);
      }
    };
    
    if (user) {
      fetchUserPlan();
    }
  }, [user]);

  const menuItems = [
    { path: '/', icon: Home, label: t('menu.home') },
    { path: '/create', icon: Plus, label: t('menu.addRecipe') },
    { path: '/ai-generator', icon: Sparkles, label: t('menu.createWithAI') },
    { path: '/chat-assistant', icon: MessageCircle, label: t('menu.assistant') },
    { path: '/meal-planner', icon: Calendar, label: t('menu.mealPlanner') },
  ];
  
  // Adicionar Notificações no mobile
  const mobileMenuItems = [
    ...menuItems,
    { 
      path: user?.role === 'admin' ? '/admin/messages' : '/contact', 
      icon: Bell, 
      label: '🔔 Notificações',
      badge: notificationCount 
    }
  ];
  
  // Adicionar item Admin se for admin (apenas desktop)
  if (user?.role === 'admin') {
    menuItems.push({ path: '/admin', icon: Users, label: '🔐 Admin' });
  }

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    
    return (
      <Button
        key={item.path}
        onClick={() => {
          navigate(item.path);
          setMobileMenuOpen(false);
        }}
        variant={isActive ? 'default' : 'ghost'}
        className={`${
          isActive 
            ? 'bg-orange-600 hover:bg-orange-700 text-white' 
            : 'hover:bg-orange-50 text-gray-700'
        } flex items-center gap-2 w-full md:w-auto justify-start md:justify-center`}
      >
        <Icon className="w-4 h-4" />
        <span>{item.label}</span>
      </Button>
    );
  };

  return (
    <div className="bg-white/70 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-20 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Desktop Menu */}
        <div className="hidden md:block">
          {/* Top Row - Logo Space + Actions */}
          <div className="flex items-center justify-between py-2 border-b border-gray-200/30">
            {/* Left Spacer for Logo - Responsivo */}
            <div className="w-32 lg:w-48"></div>

            {/* Right Actions */}
            <div className="flex items-center gap-1 lg:gap-2">
              {/* Modo Vovó Toggle */}
              <div
                onClick={toggleAccessibility}
                className="vovo-toggle-desktop"
                title={accessibilityMode ? t('accessibility.disable') : t('accessibility.enable')}
              >
                <img 
                  src="/vovo-icon.png" 
                  alt="Modo Vovó"
                />
              </div>
              
              {/* Language Selector */}
              <LanguageSelector />
              
              {/* Notification Bell */}
              <NotificationBell />
              
              {/* Premium Button - Icon only */}
              <Button
                onClick={() => navigate('/subscription')}
                size="icon"
                className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-md"
                title="Premium"
              >
                <Crown className="w-4 h-4" />
              </Button>

              {/* Divider */}
              <div className="w-px h-8 bg-gray-300 mx-1"></div>
              
              {/* Profile Button */}
              <Button
                onClick={() => navigate('/profile')}
                variant={location.pathname === '/profile' ? 'default' : 'ghost'}
                size="icon"
                className={`${
                  location.pathname === '/profile'
                    ? 'bg-orange-600 hover:bg-orange-700 text-white'
                    : 'hover:bg-orange-50 text-gray-700'
                }`}
                title={user?.name || 'Perfil'}
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                ) : (
                  <User className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Bottom Row - Navigation Items - Responsivo */}
          <div className="flex items-center justify-center gap-1 md:gap-2 lg:gap-3 py-2 px-20 md:px-24 lg:px-4">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  variant={isActive ? 'default' : 'ghost'}
                  className={`${
                    isActive 
                      ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-md' 
                      : 'hover:bg-orange-50 text-gray-700'
                  } flex items-center gap-1 lg:gap-2 text-xs lg:text-sm px-2 lg:px-4`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden lg:inline">{item.label}</span>
                  <span className="lg:hidden text-[10px]">{item.label.split(' ')[0]}</span>
                </Button>
              );
            })}
          </div>
          
          {/* Floating Logo - Responsivo */}
          <div 
            onClick={() => navigate('/')}
            className="floating-logo absolute left-4 md:left-6 lg:left-8 top-1/2 -translate-y-1/2 cursor-pointer hover:opacity-90 transition-all hover:scale-105 z-10"
          >
            <img src="/logo.png" alt="mAI Recipes" className="w-[6rem] md:w-[7rem] lg:w-[8.64rem] h-[6rem] md:h-[7rem] lg:h-[8.64rem] object-contain" />
          </div>
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden">
          {/* Top Row */}
          <div className="flex items-center justify-between py-3 border-b border-gray-200/30">
            {/* Logo Only */}
            <div 
              onClick={() => navigate('/')}
              className="mobile-logo-container cursor-pointer hover:opacity-80 transition-opacity"
            >
              <img 
                src="/logo.png" 
                alt="mAI Recipes" 
                className="object-contain"
                style={{ width: '5.469rem', height: '5.469rem' }}
              />
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Premium */}
              <Button
                onClick={() => navigate('/subscription')}
                size="icon"
                className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-md"
                title="Premium"
              >
                <Crown className="w-5 h-5" />
              </Button>

              {/* Modo Vovó Toggle */}
              <div
                onClick={toggleAccessibility}
                className="vovo-toggle-mobile"
                title={accessibilityMode ? t('accessibility.disable') : t('accessibility.enable')}
              >
                <img 
                  src="/vovo-icon.png" 
                  alt="Modo Vovó"
                />
              </div>

              {/* Profile Button - Always visible on mobile */}
              <Button
                onClick={() => navigate('/profile')}
                variant={location.pathname === '/profile' ? 'default' : 'ghost'}
                size="icon"
                className={`${
                  location.pathname === '/profile'
                    ? 'bg-orange-600 hover:bg-orange-700 text-white'
                    : 'hover:bg-orange-50 text-gray-700'
                }`}
                title={user?.name || 'Perfil'}
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                ) : (
                  <User className="w-5 h-5" />
                )}
              </Button>

              {/* Toggle Menu Button com Badge */}
              <Button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                variant="outline"
                size="icon"
                className="border-gray-300 relative"
              >
                {mobileMenuOpen ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Mobile Menu Items - Dropdown */}
          {mobileMenuOpen && (
            <div className="mobile-menu-dropdown pb-3 space-y-2 border-t border-gray-200/30 pt-3 bg-white/50 backdrop-blur-sm">
              <div className="space-y-2">
                {mobileMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <div
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        setMobileMenuOpen(false);
                      }}
                      className={`mobile-menu-item flex items-center gap-3 px-4 py-3 cursor-pointer transition-all ${
                        isActive 
                          ? 'bg-orange-600 text-white rounded-lg' 
                          : 'hover:bg-orange-50 rounded-lg'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="ml-auto bg-red-600 text-white text-xs rounded-full px-2 py-0.5 font-bold">
                          {item.badge}
                        </span>
                      )}
                    </div>
                  );
                })}
                
                {/* Language Selector for Mobile */}
                <div className="px-2 pt-2 border-t border-gray-200/30">
                  <LanguageSelector />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NavigationMenu;